/** Where the prompt or project is primarily used */
export const WHERE_TO_USE = {
  CHAT: 'chat',
  TOOLS: 'tools',
};

export const WHERE_TO_USE_OPTIONS = [
  { id: WHERE_TO_USE.CHAT, label: 'Chat assistants' },
  { id: WHERE_TO_USE.TOOLS, label: 'Coding / agent tools' },
];

/** @type {{ id: string, label: string }[]} */
export const CHAT_USAGE_PLATFORMS = [
  { id: 'promptcrew', label: 'PromptCrew' },
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'claude', label: 'Claude' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'copilot', label: 'Microsoft Copilot' },
  { id: 'slack', label: 'Slack' },
  { id: 'meta_ai', label: 'Meta AI' },
  { id: 'other', label: 'Other' },
];

/** @type {{ id: string, label: string }[]} */
export const TOOLS_USAGE_PLATFORMS = [
  { id: 'claude', label: 'Claude' },
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'copilot', label: 'Microsoft Copilot' },
  { id: 'slack', label: 'Slack' },
  { id: 'codex', label: 'Codex' },
  { id: 'cursor', label: 'Cursor' },
  { id: 'kiro', label: 'Kiro' },
  { id: 'claude_code', label: 'Claude Code' },
  { id: 'claude_cowork', label: 'Claude Cowork' },
  { id: 'perplexity', label: 'Perplexity' },
  { id: 'vscode', label: 'VS Code' },
  { id: 'windsurf', label: 'Windsurf' },
  { id: 'zapier', label: 'Zapier' },
  { id: 'make', label: 'Make' },
  { id: 'n8n', label: 'n8n' },
  { id: 'pipedream', label: 'Pipedream' },
  { id: 'aws_lambda', label: 'AWS Lambda' },
  { id: 'other', label: 'Other' },
];

export const USAGE_PLATFORM_OTHER = 'other';

export const DEFAULT_WHERE_TO_USE = WHERE_TO_USE.CHAT;

/** Default platform when targeting chat */
export const DEFAULT_USAGE_PLATFORM_CHAT = 'promptcrew';

/** Default platform when targeting tools */
export const DEFAULT_USAGE_PLATFORM_TOOLS = 'cursor';

const CHAT_IDS = new Set(CHAT_USAGE_PLATFORMS.map((p) => p.id));
const TOOLS_IDS = new Set(TOOLS_USAGE_PLATFORMS.map((p) => p.id));

const USAGE_PLATFORM_ALIASES = {
  'promptcrew only': 'promptcrew',
  'promptcrew chat': 'promptcrew',
  'promptcrew creator': 'promptcrew',
  prompt_crew: 'promptcrew',
  'prompt crew': 'promptcrew',
  'chat gpt': 'chatgpt',
  openai: 'chatgpt',
  'microsoft copilot': 'copilot',
  meta: 'meta_ai',
  metaai: 'meta_ai',
  'meta ai': 'meta_ai',
  'claude code': 'claude_code',
  claudecode: 'claude_code',
  'claude cowork': 'claude_cowork',
  claudecowork: 'claude_cowork',
  codex: 'codex',
  'vs code': 'vscode',
  'visual studio code': 'vscode',
};

export function normalizeUsagePlatformId(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return '';
  const compact = raw.replace(/[\s-]+/g, '_');
  return USAGE_PLATFORM_ALIASES[raw] || USAGE_PLATFORM_ALIASES[compact] || compact;
}

export function usagePlatformsForWhere(whereToUse) {
  return whereToUse === WHERE_TO_USE.TOOLS ? TOOLS_USAGE_PLATFORMS : CHAT_USAGE_PLATFORMS;
}

/** Platforms are chosen by capability type in the builder. */
export function usagePlatformsForCapabilityType(capabilityType, whereToUse) {
  if (capabilityType === 'skill' || capabilityType === 'agent') return TOOLS_USAGE_PLATFORMS;
  return usagePlatformsForWhere(whereToUse);
}

export function defaultUsagePlatformForWhere(whereToUse) {
  return whereToUse === WHERE_TO_USE.TOOLS ? DEFAULT_USAGE_PLATFORM_TOOLS : DEFAULT_USAGE_PLATFORM_CHAT;
}

export function isUsagePlatformAllowed(whereToUse, platformId, capabilityType) {
  const set = capabilityType === 'skill' || capabilityType === 'agent'
    ? TOOLS_IDS
    : whereToUse === WHERE_TO_USE.TOOLS ? TOOLS_IDS : CHAT_IDS;
  return typeof platformId === 'string' && set.has(platformId);
}

export const MAX_USAGE_PLATFORM_OTHER_LENGTH = 240;

/**
 * Coerce usage fields for the client. Does not throw; trims and fixes unknown platforms.
 */
export function coerceUsageMetadata({
  whereToUse,
  usagePlatform,
  usagePlatformOther,
  capabilityType,
  allowEmptyPlatform = false,
} = {}) {
  const w = capabilityType === 'skill' || capabilityType === 'agent'
    ? WHERE_TO_USE.TOOLS
    : whereToUse === WHERE_TO_USE.TOOLS ? WHERE_TO_USE.TOOLS : WHERE_TO_USE.CHAT;
  let p = normalizeUsagePlatformId(usagePlatform);
  const allowed = capabilityType === 'skill' || capabilityType === 'agent'
    ? TOOLS_IDS
    : w === WHERE_TO_USE.TOOLS ? TOOLS_IDS : CHAT_IDS;
  if (!allowed.has(p)) {
    p = allowEmptyPlatform ? '' : defaultUsagePlatformForWhere(w);
  }
  let o = typeof usagePlatformOther === 'string' ? usagePlatformOther.trim() : '';
  if (p !== USAGE_PLATFORM_OTHER) {
    o = '';
  }
  if (o.length > MAX_USAGE_PLATFORM_OTHER_LENGTH) {
    o = o.slice(0, MAX_USAGE_PLATFORM_OTHER_LENGTH);
  }
  return {
    whereToUse: w,
    usagePlatform: p,
    usagePlatformOther: o,
  };
}

/**
 * Human-readable label for where a prompt is intended to run.
 */
export function formatUsagePlatformLabel({ whereToUse, usagePlatform, usagePlatformOther } = {}) {
  const normalized = coerceUsageMetadata({ whereToUse, usagePlatform, usagePlatformOther });
  if (normalized.usagePlatform === USAGE_PLATFORM_OTHER) {
    return normalized.usagePlatformOther || 'Custom platform or tool';
  }
  const platforms = usagePlatformsForWhere(normalized.whereToUse);
  const match = platforms.find((option) => option.id === normalized.usagePlatform);
  return match?.label || '';
}
